import { gpsIcon } from "assets/icons";
import { Select } from "components/form";
import React, { useEffect, useState } from "react";
import "../../../userDashboard/pages/trackOrder/TrackOrder.scss";
import {
  FaChevronDown,
  FaChevronUp,
  FaRegFileLines,
  FaBoxOpen,
  FaTruck,
  FaLocationDot,
  FaCircleCheck,
  FaXmark,
  FaRotateLeft,
  FaCircleExclamation,
} from "react-icons/fa6";
import { getOrderDetailsForTrackAPI } from "services/user-controllers/order-controller";
import { toast } from "react-toastify";
import moment from "moment";
import { getOrderDetailsForTrackHomeAPI } from "services/user-controllers/masterController";
import CustomModal from "components/modals/CustomModal";

// flatten every item's status-remark history, sort by createdAt (newest
// first), and return the userDelayReason of the most recent entry that
// actually has one — no falling back to remark/delayReason text
const getLatestRemarkText = (orderItemList) => {
  const allRemarks = (orderItemList || []).flatMap((item) =>
    (item?.orderedItemsStatus || []).flatMap(
      (status) => status?.statusRemark || []
    )
  );

  const sorted = [...allRemarks].sort(
    (a, b) => new Date(b?.createdAt) - new Date(a?.createdAt)
  );

  const latest = sorted.find((r) => r?.userDelayReason?.trim());

  return latest?.userDelayReason?.trim() || "";
};

const TrackOrderHome = ({ trackResp }) => {
  // Function for generate random Id
  const generateId = () => Math.floor(Math.random() * 10000);

  // `statusId` is the real mvOrderStatusType id (used to pull remarks for that
  // status via getStatusTimeline). `rank` is a separate display order used
  // for the pill row's "reached" comparison below — needed because OFD(7)/
  // Lost(8) were added to the status table after Delivered(4), so their raw
  // ids don't reflect where they actually fall in the journey (OFD happens
  // before Delivered, Lost is a terminal like Cancelled/RTO).
  const statusArray = [
    {
      statusId: 1,
      rank: 1,
      id: generateId(),
      statusName: "Booked",
      statusLabel: "Your Order is Booked",
      pillLabel: "Order booked",
      icon: <FaRegFileLines />,
    },
    {
      statusId: 2,
      rank: 2,
      id: generateId(),
      statusName: "PickedUp",
      statusLabel: "Your order is pickedup",
      pillLabel: "Picked up",
      icon: <FaBoxOpen />,
    },
    {
      statusId: 3,
      rank: 3,
      id: generateId(),
      statusName: "InTransit",
      statusLabel: "Your order is In Transit",
      pillLabel: "In transit",
      icon: <FaTruck />,
    },
    {
      statusId: 7,
      rank: 4,
      id: generateId(),
      statusName: "OFD",
      statusLabel: "Your order is Out for Delivery",
      pillLabel: "Out for delivery",
      icon: <FaLocationDot />,
    },
    {
      statusId: 4,
      rank: 5,
      id: generateId(),
      statusName: "Delivered",
      statusLabel: "Your order is Delivered",
      pillLabel: "Delivered",
      icon: <FaCircleCheck />,
    },
    {
      statusId: 5,
      rank: 6,
      id: generateId(),
      statusName: "Cancelled",
      statusLabel: "Your order is Cancelled",
      pillLabel: "Cancelled",
      icon: <FaXmark />,
    },
    {
      statusId: 6,
      rank: 7,
      id: generateId(),
      statusName: "RTO",
      statusLabel: "Your order is RTO",
      pillLabel: "RTO",
      icon: <FaRotateLeft />,
    },
    {
      statusId: 8,
      rank: 8,
      id: generateId(),
      statusName: "Lost",
      statusLabel: "Your order is Lost",
      pillLabel: "Lost",
      icon: <FaCircleExclamation />,
    },
  ];
  const rankByStatusId = statusArray.reduce((acc, s) => {
    acc[s.statusId] = s.rank;
    return acc;
  }, {});

  //************************* Handle all state *************************//

  const [orderNumber, setOrderNumber] = useState("");
  const [orderDetails, setOrderDetails] = useState({});
  const [orderItemList, setOrderItemList] = useState([]);
  const [totalRecordCount, setTotalRecordCount] = useState(0);
  const [selectedItemDetails, setSelectedItemDetails] = useState({});
  // which status pill is currently open in the timeline below — kept
  // separate from selectedItemDetails.statusId so a user can click back to
  // an earlier status without losing track of where the item actually is
  const [selectedStatusId, setSelectedStatusId] = useState(null);

  const [remarkArray, setRemarkArray] = useState([]);
  const [showTrackModal, setShowTrackModal] = useState(false);

  // close track details modal
  const closeTrackModal = () => setShowTrackModal(false);

  // whenever the selected item changes (item switch or a fresh search),
  // auto-open the pill for whatever status that item is actually at
  useEffect(() => {
    if (selectedItemDetails?.statusId != null) {
      setSelectedStatusId(selectedItemDetails.statusId);
    }
  }, [selectedItemDetails]);

  //************************* Handle all function *************************//
  // get orderAndItemDetailsHandler
  const orderAndItemDetailsHandler = async () => {
    try {
      const { data } = await getOrderDetailsForTrackHomeAPI(orderNumber);
      if (data.code === 200) {
        setOrderItemList(data?.data?.list);
        setOrderDetails(data?.data?.details);
        setTotalRecordCount(data?.data?.count);
        let orderItemStatusData = {
          statusId: data?.data?.list[0]?.Itemstatus?.id,
          statusName: data?.data?.list[0]?.Itemstatus?.name,
          statusArray: data?.data?.list[0]?.orderedItemsStatus,
        };
        setSelectedItemDetails(orderItemStatusData);
      } else {
        toast.error(data?.message);
      }
      console.log("I AM ORDER DETAILS DATA", data);
    } catch (err) {
      console.error(err);
    }
  };

  // Builds the vertical "updates" timeline for one status pill — one entry
  // per remark row recorded against that status (e.g. every throttled
  // in-transit scan), newest first isn't required here since remarks are
  // already stored/returned in the order they occurred.
  const getStatusTimeline = (statusId) => {
    const statusObj = selectedItemDetails?.statusArray?.find(
      (el) => el?.StatusType === statusId
    );
    if (!statusObj?.ItemId) return [];

    const remarks = (statusObj?.statusRemark || []).filter(
      (el) => el?.remark && el.remark.trim()
    );
    if (!remarks.length) return [];

    return remarks.map((el) => ({
      id: el?.id,
      title: el?.remark,
    }));
  };

  // Only show pills for statuses this item actually has a record for — e.g.
  // if it has only reached Booked -> PickedUp -> InTransit so far, don't
  // show OFD/Delivered/Cancelled/RTO/Lost pills at all yet.
  const availableStatusArray = statusArray.filter((el) =>
    selectedItemDetails?.statusArray?.some(
      (s) => s?.StatusType === el.statusId && s?.ItemId
    )
  );

  // check if delay reason should show (ETA passed and a reason is available,
  // regardless of whether the order has since been delivered)
  const isOrderDelayed = () => {
    if (!orderDetails?.ETA) return false;
    const isPastETA = moment().isAfter(moment(orderDetails?.ETA).endOf("day"));
    return isPastETA && !!getLatestRemarkText(orderItemList);
  };

  // onChange Handler for item select
  const orderItemOnChangeHandler = (e) => {
    const { value } = e.target;
    console.log("I AM VALUE", value);
    const findOrderItemObj = orderItemList.find((el) => el?.Itemid === value);
    if (findOrderItemObj?.Itemid?.length > 0) {
      let orderItemStatusData = {
        statusId: findOrderItemObj?.Itemstatus?.id,
        statusName: findOrderItemObj?.Itemstatus?.name,
        statusArray: findOrderItemObj?.orderedItemsStatus,
      };
      setSelectedItemDetails(orderItemStatusData);
    }
  };

  // userEffect for update States or track order details
  useEffect(() => {
    if (trackResp.code === 200) {
      const statusRemarkArray = trackResp?.data?.list?.map((el) => {
        return el?.orderedItemsStatus;
      });
      setRemarkArray(statusRemarkArray);

      setOrderItemList(trackResp?.data?.list);
      setOrderDetails(trackResp?.data?.details);
      setTotalRecordCount(trackResp?.data?.count);
      let orderItemStatusData = {
        statusId: trackResp?.data?.list[0]?.Itemstatus?.id,
        statusName: trackResp?.data?.list[0]?.Itemstatus?.name,
        statusArray: trackResp?.data?.list[0]?.orderedItemsStatus,
      };
      setSelectedItemDetails(orderItemStatusData);
    } else {
      toast.error(trackResp?.message);
    }
  }, [trackResp]);

  console.log("REMARK ARRAY =====>>>>>>>>>>>>>", remarkArray);

  //************************* Handle HTML CODE *************************//
  return (
    <>
      <div className="track_order">
        {/* <div class="dashboard_title">
          <h4>Track Your Order</h4>
        </div> */}

        {/* <div className="track_order_wrap">
          <div className="search_wrap">
            <img src={gpsIcon} alt="" />
            <input
              type="search"
              value={orderNumber}
              className="input form-control"
              placeholder="Enter Order Number"
              onChange={(e) => setOrderNumber(e?.target?.value)}
            />

            <Button onClick={orderAndItemDetailsHandler}>Search Now</Button>
          </div>
        </div> */}

        {/*-------------------- Order Details and Status Details ------------------*/}
        {totalRecordCount > 0 ? (
          <>
            <div className="order_detail _page">
              <ul>
                <li>
                  <h4>Order ID{" "}:{" "} {orderNumber || orderDetails?.orderId}{orderDetails?.LSPDocketNo ? <>{" "}||{" "}{orderDetails?.LSPDocketNo}</> : ""}</h4>
                  <h4>Shipment Status{" "}:{" "} {orderDetails?.status || "-----"}</h4>
                </li>
                <li>
                  <span>Number of Package{" "}:{" "} {totalRecordCount || 0}</span>
                  <span>
                    Latest Remark{" "}:{" "}
                    {orderDetails?.latestRemark?.ExcelRemarks ||
                      "------"}
                  </span>
                </li>
                <li>
                  <span>
                    Booked on {" "}:{" "}
                    {moment(orderDetails?.createdAt).format("MMM DD, YYYY") ||
                      "------"}
                  </span>
                  <span>
                    ETA {" "}:{" "}
                    {moment(orderDetails?.ETA).format("MMM DD, YYYY") ||
                      "------"}
                  </span>
                </li>
                <li>
                  <span>Consignor Name{" "}:{" "}  {orderDetails?.accountName || "------"}</span>
                  <span>
                    Consignee Name{" "}:{" "}
                    {orderDetails?.deliverypersonname ||
                      "------"}
                  </span>
                </li>
                <li>
                  <span>
                    Origin Pin{" "}:{" "}
                    {orderDetails?.Frompincode ||
                      "------"}
                  </span>
                  <span>Origin State{" "}:{" "} {orderDetails?.fromState || "------"}</span>

                </li>
                <li>
                  <span>
                    Destination Pin {" "}:{" "}
                    {orderDetails?.ToPincode ||
                      "------"}
                  </span>
                  <span>Destination State {" "}:{" "}{orderDetails?.toState || "------"}</span>

                </li>
                <li className="d-flex justify-content-between align-items-center">
                  {isOrderDelayed() ? (
                    <span>
                      Delay Reason{" "}:{" "}
                      {getLatestRemarkText(orderItemList) || "------"}
                    </span>
                  ) : (
                    <span></span>
                  )}

                  <div className="view_details_btn" onClick={() => setShowTrackModal(!showTrackModal)} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: "5px", whiteSpace: "nowrap" }}>
                    <h4>View detail tracking {" "}{showTrackModal ? <FaChevronUp size={14} /> : <FaChevronDown size={14} />}</h4>
                  </div>
                </li>

              </ul>
            </div>
          </>
        ) : (
          <h4>Search with order number</h4>
        )}
        {/*-------------------- Close Order Details and Status Details ------------------*/}
      </div>

      {/*-------------------- Track Order Details Modal ------------------*/}
      <CustomModal
        show={showTrackModal}
        handleClose={closeTrackModal}
        className="xl track_order_modal"
      >
        <div className="track_order">
          <div className="order_status _page">
            <div className="has_child">
              <h4>Track Order</h4>
              <Select onChange={orderItemOnChangeHandler}>
                {orderItemList?.length > 0 &&
                  orderItemList?.map((el) => (
                    <option key={el?.id} value={el?.Itemid}>
                      {el?.Itemid}
                    </option>
                  ))}
              </Select>
            </div>

            <div className="status_progress">
              <ul className="status_pill_row">
                {availableStatusArray?.length > 0 &&
                  availableStatusArray.map((el) => {
                    const currentRank =
                      rankByStatusId[selectedItemDetails?.statusId] || 0;
                    const isReached = el.rank <= currentRank;
                    const isSelected = selectedStatusId === el.statusId;
                    return (
                      <li key={el.id}>
                        <button
                          type="button"
                          className={`status_pill${isReached ? " reached" : ""}${isSelected ? " selected" : ""
                            }`}
                          onClick={() => setSelectedStatusId(el.statusId)}
                        >
                          <span className="pill_icon">{el.icon}</span>
                          {el.pillLabel}
                        </button>
                      </li>
                    );
                  })}
              </ul>

              {(() => {
                const activeStatus = availableStatusArray.find(
                  (el) => el.statusId === selectedStatusId
                );
                const timeline = getStatusTimeline(selectedStatusId);
                return (
                  <div className="status_updates">
                    <h5>{activeStatus?.pillLabel || "-----"} — updates</h5>
                    {timeline.length > 0 ? (
                      <ul className="update_timeline">
                        {timeline.map((entry) => (
                          <li key={entry.id}>
                            <span className="dot" />
                            <div className="content">
                              <b>{entry.title}</b>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="empty">No updates yet</p>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </CustomModal>
    </>
  );
};

export default TrackOrderHome;
